import React, { Dispatch, SetStateAction, useEffect } from 'react';

import {
  Box,
  FilledTextFieldProps,
  FormControlLabel,
  InputAdornment,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
  TextField,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { NotificationFormikState } from 'components/Common/NotificationSettingsModal';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { useTextfieldStyles } from 'components/Common/ValidatedTextField';
import { JBColors, JBIcon } from '@bjb-gcrm/gcrm-frontend-components';

export const checkIfDateIsValid = (checkedDate: any) => checkedDate instanceof Date && !isNaN(checkedDate.valueOf());

type Props = {
  selectedDate: [null | Date, null | Date];
  setSelectedDate: Dispatch<SetStateAction<[null | Date, null | Date]>>;
  startDateFieldName?: keyof NotificationFormikState;
  endDateFieldName?: keyof NotificationFormikState;
  disabled: boolean;
  useFormik?: boolean;
  label?: string;
};

export const CustomDateRangePicker = ({
  selectedDate,
  setSelectedDate,
  startDateFieldName = 'fwdStartDate',
  endDateFieldName = 'fwdEndDate',
  useFormik = true,
  disabled,
  label,
}: Props) => {
  const formikContext = useFormikContext<NotificationFormikState>();
  const textFieldStyles = useTextfieldStyles();

  useEffect(() => {
    if (useFormik) {
      formikContext.setFieldValue(startDateFieldName, selectedDate[0]);
      formikContext.setFieldValue(endDateFieldName, selectedDate[1]);
    }
  }, [selectedDate, formikContext?.setFieldValue]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      dateFormats={{ normalDate: 'dd.MM.yyyy', keyboardDate: 'dd.MM.yyyy' }}
    >
      <FormControlLabel
        label={label ?? ''}
        classes={{
          root: textFieldStyles.labelRoot,
          labelPlacementTop: textFieldStyles.labelPlacementTop,
        }}
        labelPlacement="top"
        control={
          <DateRangePicker
            disabled={disabled}
            startText=""
            endText=""
            mask="__.__.____"
            value={selectedDate}
            onChange={(date: React.SetStateAction<[Date | null, Date | null]>) => {
              setSelectedDate(date);
              if (useFormik) {
                formikContext.setFieldError(startDateFieldName, undefined);
                formikContext.setFieldError(endDateFieldName, undefined);
              }
            }}
            renderInput={(
              startProps: StandardTextFieldProps | FilledTextFieldProps | OutlinedTextFieldProps,
              endProps: StandardTextFieldProps | FilledTextFieldProps | OutlinedTextFieldProps,
            ) => (
              <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                <div>
                  <TextField
                    {...startProps}
                    onChange={(event) => {
                      const startDateString = event.target.value;
                      const startDate = new Date(startDateString);
                      setSelectedDate([startDate, selectedDate[1]]);
                      if (useFormik) {
                        formikContext.setFieldValue(startDateFieldName, startDate);
                      }
                    }}
                    inputProps={{ ...startProps.inputProps, 'data-testid': 'date-range-filter-from-date-input' }}
                    data-testid="date-range-filter-from-date"
                    variant="filled"
                    margin="dense"
                    className={textFieldStyles.textInput}
                    helperText=""
                    error={!!formikContext?.errors[startDateFieldName] && formikContext?.touched[startDateFieldName]}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" disablePointerEvents>
                          <JBIcon iconName="calendar" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <Box m={1.5} color={JBColors.support.greenSmoke[100]}>
                  to
                </Box>
                <div>
                  <TextField
                    {...endProps}
                    onChange={(event) => {
                      const endDateString = event.target.value;
                      const endDate = new Date(endDateString);
                      setSelectedDate([selectedDate[0], endDate]);
                      if (useFormik) {
                        formikContext.setFieldValue(endDateFieldName, endDate);
                      }
                    }}
                    inputProps={{ ...endProps.inputProps, 'data-testid': 'date-range-filter-to-date-input' }}
                    variant="filled"
                    data-testid="date-range-filter-to-date"
                    margin="dense"
                    className={textFieldStyles.textInput}
                    helperText=""
                    error={!!formikContext?.errors[endDateFieldName] && formikContext?.touched[endDateFieldName]}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" disablePointerEvents>
                          <JBIcon iconName="calendar" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </Box>
            )}
          />
        }
      />
    </LocalizationProvider>
  );
};

export default CustomDateRangePicker;
